/*
 * Cart Addon Upsells — click handler for the Priority Processing +
 * per-mug Gift Wrapping strip rendered by snippets/cart-addon-upsells.liquid
 * inside the cart drawer.
 *
 * Loaded as an external asset from layout/theme.liquid so this binds
 * ONCE at page load, outside the drawer's AJAX-replaced section. The
 * drawer's inline <script> tag can't be relied on — Symmetry's cart
 * drawer lazy-loads its HTML via a section fetch, and browsers refuse
 * to execute <script> tags injected via innerHTML (security measure).
 * External script + document-level event delegation works regardless
 * of when the checkbox DOM nodes were rendered or re-rendered.
 */
(function () {
  if (window.__cartAddonsBound) return;
  window.__cartAddonsBound = true;

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function propsOf(item) {
    return item.properties || {};
  }

  function findPriorityLine(cart, productId) {
    return (cart.items || []).find(function (it) {
      return String(it.product_id) === String(productId);
    });
  }

  function findGiftWrapLineFor(cart, productId, mugVariantId) {
    return (cart.items || []).find(function (it) {
      if (String(it.product_id) !== String(productId)) return false;
      const props = propsOf(it);
      return String(props._wraps_variant_id || '') === String(mugVariantId);
    });
  }

  function refreshCart() {
    // Primary refresh path — Symmetry's <cart-form> custom element
    // exposes a refresh() method that re-fetches its section HTML
    // and swaps it in. This is what the theme's own add-to-cart /
    // quantity controls call. Our custom events alone weren't enough
    // for the removal path (the drawer updated after an ADD because
    // the theme's XHR interceptor caught /cart/add.js, but didn't
    // intercept /cart/change.js). Calling refresh() directly covers
    // both paths uniformly.
    const cartForm = document.querySelector('cart-form');
    if (cartForm && typeof cartForm.refresh === 'function') {
      cartForm.refresh();
    }
    // Belt-and-braces events for any other theme code that listens.
    document.dispatchEvent(new CustomEvent('theme:cart:change'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function handleToggle(checkbox) {
    const card = checkbox.closest('[data-cart-addon-card]');
    if (!card || card.classList.contains('cart-addon-card--busy')) return;
    card.classList.add('cart-addon-card--busy');

    const kind      = checkbox.dataset.cartAddonKind;
    const variantId = checkbox.dataset.variantId;
    const productId = checkbox.dataset.productId;
    const checked   = checkbox.checked;

    const action = checked
      ? addLine(kind, variantId, checkbox)
      : removeLine(kind, productId, checkbox);

    action
      .then(refreshCart)
      .catch(function (err) {
        console.error('[cart-addons]', err);
        checkbox.checked = !checked;
      })
      .finally(function () { card.classList.remove('cart-addon-card--busy'); });
  }

  function addLine(kind, variantId, checkbox) {
    const properties = {};
    if (kind === 'giftwrap') {
      properties['For']               = checkbox.dataset.mugLabel || '';
      properties['_wraps_variant_id'] = checkbox.dataset.mugVariantId || '';
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1, properties: properties }] }),
    }).then(function (r) {
      if (!r.ok) throw new Error('/cart/add.js returned ' + r.status);
    });
  }

  function removeLine(kind, productId, checkbox) {
    return fetchCart().then(function (cart) {
      let line;
      if (kind === 'priority') {
        line = findPriorityLine(cart, productId);
      } else {
        line = findGiftWrapLineFor(cart, productId, checkbox.dataset.mugVariantId);
      }
      if (!line) return;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      }).then(function (r) {
        if (!r.ok) throw new Error('/cart/change.js returned ' + r.status);
      });
    });
  }

  function sweepOrphans() {
    fetchCart().then(function (cart) {
      const mugVariantIds = new Set(
        (cart.items || [])
          .filter(function (it) { return !(propsOf(it)._wraps_variant_id); })
          .map(function (it) { return String(it.variant_id); })
      );
      const orphans = (cart.items || []).filter(function (it) {
        const wraps = String(propsOf(it)._wraps_variant_id || '');
        return wraps && !mugVariantIds.has(wraps);
      });
      if (orphans.length === 0) return;
      const changes = orphans.reduce(function (acc, it) {
        acc[it.key] = 0;
        return acc;
      }, {});
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: changes }),
      })
        .then(refreshCart)
        .catch(function (err) { console.error('[cart-addons] orphan sweep', err); });
    });
  }

  // Delegated change listener — catches every addon checkbox toggle
  // regardless of when or how often the drawer re-renders.
  document.addEventListener('change', function (ev) {
    const cb = ev.target;
    if (!cb || !cb.matches || !cb.matches('[data-cart-addon-toggle]')) return;
    handleToggle(cb);
  });

  // Info-tooltip (i) button — click reveals/hides the explanation panel
  // below the card. Document-level delegation so the handler survives
  // the drawer's section-replacement re-renders. The button lives OUTSIDE
  // the <label> in the DOM, so clicking it doesn't toggle the checkbox.
  document.addEventListener('click', function (ev) {
    const btn = ev.target && ev.target.closest
      ? ev.target.closest('[data-cart-addon-info-toggle]')
      : null;
    if (!btn) return;
    ev.preventDefault();
    const item = btn.closest('.cart-addon-item');
    if (!item) return;
    const panel = item.querySelector('[data-cart-addon-info]');
    if (!panel) return;
    const isOpen = !panel.hasAttribute('hidden');
    if (isOpen) {
      panel.setAttribute('hidden', '');
      btn.setAttribute('aria-expanded', 'false');
    } else {
      panel.removeAttribute('hidden');
      btn.setAttribute('aria-expanded', 'true');
    }
  });

  // Clean orphan wraps whenever the cart mutates (e.g. a mug removed
  // while the drawer wasn't open).
  document.addEventListener('theme:cart:change', sweepOrphans);
  document.addEventListener('cart:refresh',       sweepOrphans);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweepOrphans);
  } else {
    sweepOrphans();
  }
})();
